import { useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Shield, ExternalLink, Loader2, LogOut } from "lucide-react";
import { useTrustConfig, useCheckSession, ApiError } from "@/api/client";
import { applyBranding } from "@/lib/theme";
import { isAuthenticated, getSession, clearSession } from "@/lib/session";

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return isActive
    ? "text-brand-primary font-semibold border-b-2 border-brand-primary pb-0.5"
    : "text-gray-600 hover:text-gray-900 pb-0.5";
}

export default function Layout() {
  const { data: trust, isLoading, error } = useTrustConfig();
  const navigate = useNavigate();

  const hasSession = isAuthenticated();
  const session = getSession();

  // Validate session against server on mount (only if we have a token)
  const { data: sessionCheck } = useCheckSession();

  // If server says session is invalid, clear local storage
  useEffect(() => {
    if (sessionCheck && !sessionCheck.valid && hasSession) {
      clearSession();
    }
  }, [sessionCheck, hasSession]);

  useEffect(() => {
    if (trust?.tenant.branding) {
      applyBranding(trust.tenant.branding);
    }
  }, [trust?.tenant.branding]);

  useEffect(() => {
    if (trust?.tenant.page_title) {
      document.title = trust.tenant.page_title;
    } else if (trust?.tenant.company_name) {
      document.title = `${trust.tenant.company_name} - Trust Center`;
    }
  }, [trust?.tenant.page_title, trust?.tenant.company_name]);

  function handleSignOut() {
    clearSession();
    navigate("/");
  }

  // Tenant not found
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4">
        <Shield className="mb-4 h-12 w-12 text-gray-300" />
        <h1 className="text-xl font-semibold text-gray-700">
          Trust Center Not Found
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          The requested trust center does not exist or is not configured.
        </p>
      </div>
    );
  }

  // Fatal error
  if (error && !(error instanceof ApiError && error.status === 404)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4">
        <Shield className="mb-4 h-12 w-12 text-red-300" />
        <h1 className="text-xl font-semibold text-gray-700">
          Unable to Load Trust Center
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          An error occurred while loading this page. Please try again later.
        </p>
      </div>
    );
  }

  const companyName = trust?.tenant.company_name ?? "";
  const logoUrl = trust?.tenant.branding?.logo_url;
  const showStatus = trust?.tenant.show_status_page ?? true;
  const showDocs = trust?.tenant.show_documents ?? false;

  // Build navigation items conditionally
  const navItems: Array<{ to: string; label: string }> = [
    { to: "/", label: "Home" },
  ];
  if (showStatus) {
    navItems.push({ to: "/status", label: "Status" });
  }
  if (showDocs) {
    navItems.push({ to: "/documents", label: "Documents" });
  }
  navItems.push({ to: "/request", label: "Request Access" });

  return (
    <div className="flex min-h-screen flex-col">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
        <div className="container-narrow">
          <div className="flex h-16 items-center justify-between">
            {/* Logo / company name */}
            <div className="flex items-center gap-3">
              {isLoading ? (
                <div className="skeleton h-8 w-32" />
              ) : logoUrl ? (
                <img
                  src={logoUrl}
                  alt={`${companyName} logo`}
                  className="h-8 max-w-[160px] object-contain"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-brand-primary" />
                  <span className="text-lg font-bold text-gray-900">
                    {companyName || "Trust Center"}
                  </span>
                </div>
              )}
            </div>

            {/* Navigation */}
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            ) : (
              <div className="flex items-center gap-4 sm:gap-6">
                <nav className="flex items-center gap-4 text-sm sm:gap-6">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      className={navLinkClass}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
                {hasSession && session && (
                  <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                    <span className="hidden text-xs text-gray-500 sm:inline">
                      {session.name}
                    </span>
                    <button
                      onClick={handleSignOut}
                      title="Sign out"
                      className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-gray-600"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      <span className="sr-only sm:not-sr-only">Sign out</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <div className="container-narrow py-6 sm:py-8">
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white py-6">
        <div className="container-narrow flex flex-col items-center justify-between gap-2 text-xs text-gray-400 sm:flex-row">
          <span>
            {companyName ? `${companyName} Trust Center` : "Trust Center"}
          </span>
          <a
            href="https://buffalosentinel.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 transition-colors hover:text-gray-600"
          >
            Powered by Buffalo Sentinel
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </footer>
    </div>
  );
}
